import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from '../hooks/useTranslation';
import { ShieldAlert, Clock, LogOut } from 'lucide-react';

/**
 * Persistenter Banner ganz oben auf jeder Seite, wenn der eingeloggte Account
 * eine Impersonation-Session ist. Zeigt:
 * - Wer impersoniert (Admin-Username)
 * - Wer wird impersoniert (Target-Username)
 * - Restzeit bis Auto-Expiry
 * - "Stop Impersonation"-Button
 */
export default function ImpersonationBanner() {
  const { t } = useTranslation();
  const { isImpersonating, impersonationInfo, stopImpersonation } = useAuth();
  const navigate = useNavigate();
  const [stopping, setStopping] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(null);

  // Restzeit-Counter
  useEffect(() => {
    if (!isImpersonating || !impersonationInfo?.expires_at) {
      setRemainingSeconds(null);
      return;
    }
    const update = () => {
      const expiresAt = new Date(impersonationInfo.expires_at).getTime();
      const now = Date.now();
      const left = Math.max(0, Math.floor((expiresAt - now) / 1000));
      setRemainingSeconds(left);
      // Kein Auto-Stop — Token läuft serverseitig ab, User sieht "0:00" und klickt selbst Button
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [isImpersonating, impersonationInfo?.expires_at]);

  const handleStop = useCallback(async () => {
    if (stopping) return;
    setStopping(true);
    try {
      const zielId = impersonationInfo?.target_user_id;
      await stopImpersonation();
      // Zurück auf die Seite des Nutzers, aus der die Sitzung gestartet wurde.
      navigate(zielId ? `/admin/users/${zielId}` : '/admin/users');
    } catch (e) {
      console.error('Stop-Impersonation failed:', e);
    } finally {
      setStopping(false);
    }
  }, [stopImpersonation, navigate, stopping, impersonationInfo?.target_user_id]);

  if (!isImpersonating || !impersonationInfo) return null;

  const formatRemaining = (sec) => {
    if (sec == null) return '';
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  return (
    <div
      role="alert"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        background: 'linear-gradient(90deg, var(--orange-500), var(--orange-600))',
        color: 'var(--weiss)',
        padding: '10px 20px',
        boxShadow: 'var(--shadow-gold)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '12px',
        fontSize: '1rem',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <ShieldAlert size={20} />
        <strong>{t('impersonation.banner.title', 'Impersonation aktiv')}</strong>
        <span style={{ opacity: 0.95 }}>
          {t('impersonation.banner.context', '{admin} arbeitet als {target}', {
            // Benutzername ist optional (NULL gültig) — dann die Adresse.
            admin: impersonationInfo.admin_username || impersonationInfo.admin_email || '?',
            target: impersonationInfo.target_username || impersonationInfo.target_email,
          })}
        </span>
        {impersonationInfo.target_username && (
          <span style={{ opacity: 0.85, fontSize: '1rem' }}>
            ({impersonationInfo.target_email})
          </span>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {remainingSeconds != null && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '1rem',
              fontFamily: 'var(--font-mono)',
              opacity: 0.9,
            }}
            data-tooltip={t('impersonation.banner.expires_tooltip', 'Restzeit bis Auto-Stop')}
          >
            <Clock size={14} />
            {formatRemaining(remainingSeconds)}
          </span>
        )}
        <button
          type="button"
          onClick={handleStop}
          disabled={stopping}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            background: 'var(--bg-primary)',
            color: 'var(--orange-700)',
            border: 'none',
            borderRadius: 'var(--radius-sm)',
            padding: '6px 12px',
            fontWeight: '600',
            fontSize: '1rem',
            cursor: stopping ? 'wait' : 'pointer',
            opacity: stopping ? 0.6 : 1,
          }}
          data-tooltip={t('impersonation.banner.stop_tooltip', 'Beendet die Session und stellt deinen Admin-Account wieder her')}
        >
          <LogOut size={14} />
          {stopping
            ? t('impersonation.banner.stopping', 'Beende...')
            : t('impersonation.banner.stop', 'Stop & zurück zu Admin')}
        </button>
      </div>
    </div>
  );
}
